// Implements: F-IDN-027
/**
 * The writes of Equipos: create, rename and delete a team, and add, re-role and remove its
 * members. Each is sent once and re-reads the organization's lists after it, whatever its outcome,
 * so a refused write never leaves a control showing a value the server did not keep.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { TeamRole, TeamSeat, TeamView } from "../../../types/organization";
import {
  addTeamMember,
  createTeam,
  deleteTeam,
  removeTeamMember,
  renameTeam,
  setTeamMemberRole,
} from "../api/organizationApi";
import { refreshOrganization } from "./keys";

function useRefreshing<TData, TVariables>(
  organizationId: string,
  run: (variables: TVariables) => Promise<TData>,
): UseMutationResult<TData, ApiError, TVariables> {
  const queryClient = useQueryClient();
  return useMutation<TData, ApiError, TVariables>({
    mutationFn: run,
    onSettled: () => refreshOrganization(queryClient, organizationId),
  });
}

export function useCreateTeam(organizationId: string): UseMutationResult<TeamView, ApiError, string> {
  return useRefreshing(organizationId, (name: string) => createTeam(organizationId, name));
}

export function useRenameTeam(organizationId: string, teamId: string): UseMutationResult<TeamView, ApiError, string> {
  return useRefreshing(organizationId, (name: string) => renameTeam(organizationId, teamId, name));
}

export function useDeleteTeam(organizationId: string, teamId: string): UseMutationResult<void, ApiError, undefined> {
  return useRefreshing(organizationId, () => deleteTeam(organizationId, teamId));
}

export interface SeatChange {
  readonly userId: string;
  readonly role: TeamRole;
  /** Owner 2026-10-19: the lead the question named, only after "Convertir a <name> en miembro". */
  readonly replaceLeadUserId?: string;
}

export function useAddTeamMember(
  organizationId: string,
  teamId: string,
): UseMutationResult<TeamSeat, ApiError, SeatChange> {
  return useRefreshing(organizationId, ({ userId, role, replaceLeadUserId }: SeatChange) =>
    addTeamMember(organizationId, teamId, userId, role, replaceLeadUserId),
  );
}

export function useSetTeamMemberRole(
  organizationId: string,
  teamId: string,
): UseMutationResult<TeamSeat, ApiError, SeatChange> {
  return useRefreshing(organizationId, ({ userId, role, replaceLeadUserId }: SeatChange) =>
    setTeamMemberRole(organizationId, teamId, userId, role, replaceLeadUserId),
  );
}

export function useRemoveTeamMember(organizationId: string, teamId: string): UseMutationResult<void, ApiError, string> {
  return useRefreshing(organizationId, (userId: string) => removeTeamMember(organizationId, teamId, userId));
}
